import { useState, useEffect, useRef, useCallback } from "react";
import {
  mergeLiveBridgeTranscript,
  normalizeLiveBridgeTranscriptText
} from "./liveBridgeTranscript";

const LIVEBRIDGE_FREE_DRAFT_STORAGE_KEY = "LIVEBRIDGE_FREE_DRAFT_V2";

function loadLiveBridgeDraftState() {
  if (typeof window === "undefined") {
    return { mode: "organize", sender: "", englishDraft: "", chineseDraft: "" };
  }

  try {
    const raw = window.localStorage.getItem(LIVEBRIDGE_FREE_DRAFT_STORAGE_KEY);

    if (!raw) {
      return { mode: "organize", sender: "", englishDraft: "", chineseDraft: "" };
    }

    const parsed = JSON.parse(raw);
    return {
      mode: parsed?.mode === "live" ? "live" : "organize",
      sender: typeof parsed?.sender === "string" ? parsed.sender : "",
      englishDraft: typeof parsed?.englishDraft === "string" ? parsed.englishDraft : "",
      chineseDraft: typeof parsed?.chineseDraft === "string" ? parsed.chineseDraft : ""
    };
  } catch (_error) {
    return { mode: "organize", sender: "", englishDraft: "", chineseDraft: "" };
  }
}

export function useDraftState() {
  const [liveBridgeSender, setLiveBridgeSender] = useState(
    () => loadLiveBridgeDraftState().sender
  );
  const [liveBridgeDraft, setLiveBridgeDraft] = useState(
    () => loadLiveBridgeDraftState().englishDraft
  );
  const [liveBridgeChineseDraft, setLiveBridgeChineseDraft] = useState(
    () => loadLiveBridgeDraftState().chineseDraft
  );
  const [liveBridgeLatestEnglish, setLiveBridgeLatestEnglish] = useState("");
  const [liveBridgeLatestChinese, setLiveBridgeLatestChinese] = useState("");
  const [liveBridgeMode, setLiveBridgeMode] = useState(
    () => loadLiveBridgeDraftState().mode
  );

  // Refs for use inside async callbacks / recognition handlers without stale closures.
  const liveBridgeDraftRef = useRef("");
  const liveBridgeChineseDraftRef = useRef("");
  const liveBridgeLatestEnglishRef = useRef("");
  const liveBridgeLatestChineseRef = useRef("");

  // Keep localStorage in sync.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      LIVEBRIDGE_FREE_DRAFT_STORAGE_KEY,
      JSON.stringify({
        mode: liveBridgeMode,
        sender: liveBridgeSender,
        englishDraft: liveBridgeDraft,
        chineseDraft: liveBridgeChineseDraft
      })
    );
  }, [liveBridgeChineseDraft, liveBridgeDraft, liveBridgeMode, liveBridgeSender]);

  // Keep refs in sync with state so recognition callbacks always see fresh values.
  useEffect(() => { liveBridgeDraftRef.current = String(liveBridgeDraft || ""); }, [liveBridgeDraft]);
  useEffect(() => { liveBridgeChineseDraftRef.current = String(liveBridgeChineseDraft || ""); }, [liveBridgeChineseDraft]);
  useEffect(() => { liveBridgeLatestEnglishRef.current = String(liveBridgeLatestEnglish || ""); }, [liveBridgeLatestEnglish]);
  useEffect(() => { liveBridgeLatestChineseRef.current = String(liveBridgeLatestChinese || ""); }, [liveBridgeLatestChinese]);

  // Called by useRecognition when a final recognition result arrives.
  const onRecognitionFinal = useCallback((transcript, languageCode) => {
    const lang = String(languageCode || "en-US").toLowerCase();

    if (lang.startsWith("zh")) {
      const next = mergeLiveBridgeTranscript(
        liveBridgeChineseDraftRef.current,
        transcript,
        lang
      );
      liveBridgeChineseDraftRef.current = next;
      setLiveBridgeChineseDraft(next);
      setLiveBridgeLatestChinese("");
      liveBridgeLatestChineseRef.current = "";
    } else {
      const next = mergeLiveBridgeTranscript(
        liveBridgeDraftRef.current,
        transcript,
        lang
      );
      liveBridgeDraftRef.current = next;
      setLiveBridgeDraft(next);
      setLiveBridgeLatestEnglish("");
      liveBridgeLatestEnglishRef.current = "";
    }
  }, []);

  // Called by useRecognition on interim results.
  const onRecognitionInterim = useCallback((transcript, languageCode) => {
    const lang = String(languageCode || "en-US").toLowerCase();
    const normalized = normalizeLiveBridgeTranscriptText(transcript, lang);

    if (lang.startsWith("zh")) {
      setLiveBridgeLatestChinese(normalized);
      liveBridgeLatestChineseRef.current = normalized;
    } else {
      setLiveBridgeLatestEnglish(normalized);
      liveBridgeLatestEnglishRef.current = normalized;
    }
  }, []);

  // Commit any pending interim text into the draft (called on stop/page-leave).
  const commitPendingRecognitionText = useCallback((languageCode) => {
    const lang = String(languageCode || "en-US").toLowerCase();

    if (lang.startsWith("zh")) {
      const next = mergeLiveBridgeTranscript(
        liveBridgeChineseDraftRef.current,
        liveBridgeLatestChineseRef.current,
        lang
      );

      if (next !== liveBridgeChineseDraftRef.current) {
        liveBridgeChineseDraftRef.current = next;
        setLiveBridgeChineseDraft(next);
      }

      liveBridgeLatestChineseRef.current = "";
      setLiveBridgeLatestChinese("");
      return;
    }

    const next = mergeLiveBridgeTranscript(
      liveBridgeDraftRef.current,
      liveBridgeLatestEnglishRef.current,
      lang
    );

    if (next !== liveBridgeDraftRef.current) {
      liveBridgeDraftRef.current = next;
      setLiveBridgeDraft(next);
    }

    liveBridgeLatestEnglishRef.current = "";
    setLiveBridgeLatestEnglish("");
  }, []);

  const clearDrafts = useCallback(() => {
    setLiveBridgeDraft("");
    setLiveBridgeChineseDraft("");
    setLiveBridgeLatestEnglish("");
    setLiveBridgeLatestChinese("");
    liveBridgeDraftRef.current = "";
    liveBridgeChineseDraftRef.current = "";
    liveBridgeLatestEnglishRef.current = "";
    liveBridgeLatestChineseRef.current = "";
  }, []);

  return {
    // State
    liveBridgeSender,
    liveBridgeDraft,
    liveBridgeChineseDraft,
    liveBridgeLatestEnglish,
    liveBridgeLatestChinese,
    liveBridgeMode,
    // Refs (needed by useNetworkSync for non-stale preview reads)
    liveBridgeDraftRef,
    liveBridgeChineseDraftRef,
    liveBridgeLatestEnglishRef,
    liveBridgeLatestChineseRef,
    // Setters
    setLiveBridgeSender,
    setLiveBridgeDraft,
    setLiveBridgeChineseDraft,
    setLiveBridgeLatestEnglish,
    setLiveBridgeLatestChinese,
    setLiveBridgeMode,
    // Callbacks for useRecognition
    onRecognitionFinal,
    onRecognitionInterim,
    commitPendingRecognitionText,
    // Utility
    clearDrafts
  };
}
